import {
  Badge,
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
} from '@monksflow/monks-ui';
import { FileSpreadsheetIcon } from 'lucide-react';
import { CONNECTOR_PLATFORM_ICONS, CONNECTOR_PLATFORM_LABELS } from '@/entities/media-connector';
import { JOB_STATUS_LABELS, type DeliveryJob, type JobStatus } from '@/entities/media-campaign';
import { TABLE_COL_WIDTH, TABLE_NAME_COL_SIDEBAR } from '@/shared/constants';
import { TruncatedText } from '@/shared/ui/TruncatedText';
import { formatShortDate } from '@/shared/utils/format';
import { DeliveryJobRowActions } from './DeliveryJobRowActions';
import { DeliveryJobSkeletonRow } from './DeliveryJobSkeletonRow';
import { useTranslation } from 'react-i18next';

const SKELETON_ROWS = Array.from({ length: 4 }, (_, i) => i);

const STATUS_VARIANT: Record<JobStatus, 'secondary' | 'warning' | 'positive' | 'destructive'> = {
  parsing: 'secondary',
  needs_review: 'warning',
  ready: 'secondary',
  publishing: 'secondary',
  published: 'positive',
  partially_published: 'warning',
  failed: 'destructive',
};

/**
 * Job columns for the SIDEBAR layout group. Platforms (160) + Contents (160) +
 * Status (128) + Created (112) = the group's 612px data region minus the 52px
 * actions column (see `table-layout.constants`; re-balance if a column
 * changes). This is the group's only remaining member.
 */
const DeliveryJobGridHeader = () => {
  const { t } = useTranslation('projects');
  return (
    <DatatableHeader>
      <DatatableRow>
        <DatatableHead scope="col" className={TABLE_NAME_COL_SIDEBAR}>
          {t('deliveryJobGrid.deliveryJobGrid.job')}
        </DatatableHead>
        <DatatableHead scope="col" className={TABLE_COL_WIDTH.type}>
          {t('deliveryJobGrid.deliveryJobGrid.source')}
        </DatatableHead>
        <DatatableHead scope="col" className={TABLE_COL_WIDTH.type}>
          {t('deliveryJobGrid.deliveryJobGrid.platforms')}
        </DatatableHead>
        <DatatableHead scope="col" className={TABLE_COL_WIDTH.type}>
          {t('deliveryJobGrid.deliveryJobGrid.contents')}
        </DatatableHead>
        <DatatableHead scope="col" className={TABLE_COL_WIDTH.statusBadge}>
          {t('deliveryJobGrid.deliveryJobGrid.status')}
        </DatatableHead>
        <DatatableHead scope="col" className={TABLE_COL_WIDTH.dateText}>
          {t('deliveryJobGrid.deliveryJobGrid.lastUpdated')}
        </DatatableHead>
        <DatatableHead scope="col" className={TABLE_COL_WIDTH.actions}>
          <span className="sr-only">{t('deliveryJobGrid.deliveryJobGrid.actions')}</span>
        </DatatableHead>
      </DatatableRow>
    </DatatableHeader>
  );
};

/**
 * The file the plan came from, as a link that hands it back.
 *
 * Worth its own column rather than a subtitle under the title: it is the thing a
 * reviewer asks a colleague for, and the one artefact of a job that exists outside the
 * app. Not a link where the job predates the source being kept — an anchor that answers
 * 404 is worse than plain text.
 */
function JobSourceLink({
  job,
  onDownload,
}: {
  job: DeliveryJob;
  onDownload?: (job: DeliveryJob) => void;
}) {
  const { t } = useTranslation('projects');
  const label = (
    <span className="flex min-w-0 items-center gap-1.5">
      <FileSpreadsheetIcon className="size-3.5 shrink-0" aria-hidden />
      <TruncatedText value={job.source.fileName} className="text-sm" />
    </span>
  );

  // Nothing to link where the job predates the file being kept: an anchor that
  // answers 404 is worse than plain text.
  if (!onDownload || !job.source.isDownloadable) {
    return <span className="text-muted-foreground">{label}</span>;
  }

  return (
    <button
      type="button"
      onClick={(event) => {
        // The row opens the job; downloading is not opening it.
        event.stopPropagation();
        onDownload(job);
      }}
      aria-label={t('deliveryJobGrid.deliveryJobGrid.downloadFile', {
        fileName: job.source.fileName,
      })}
      data-testid={`delivery-job-source-${job.id}`}
      className="flex min-w-0 items-center rounded text-muted-foreground transition-colors duration-fast ease-out hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {label}
    </button>
  );
}

export interface DeliveryJobGridProps {
  jobs: DeliveryJob[];
  isLoading: boolean;
  emptyState: React.ReactNode;
  onOpenJob: (job: DeliveryJob) => void;
  /** Absent where the list is read-only, so the menu is not offered at all. */
  onDeleteJob?: (job: DeliveryJob) => void;
  /** Absent until the job's source file is one we still hold. */
  onDownloadSource?: (job: DeliveryJob) => void;
}

export function DeliveryJobGrid({
  jobs,
  isLoading,
  emptyState,
  onOpenJob,
  onDeleteJob,
  onDownloadSource,
}: DeliveryJobGridProps) {
  const { t } = useTranslation('projects');
  const isBusy = isLoading && jobs.length === 0;

  // Column headers describe rows, so an answer with no rows leaves them
  // describing nothing. The empty state replaces the table rather than sitting
  // inside it, the way the briefs and library lists already do.
  if (!isBusy && jobs.length === 0) return <>{emptyState}</>;

  return (
    <Datatable
      aria-busy={isBusy || undefined}
      className="[&_thead_th]:border-b"
      data-testid="delivery-job-grid"
    >
      <DeliveryJobGridHeader />
      <DatatableBody>
        {isBusy
          ? SKELETON_ROWS.map((i) => <DeliveryJobSkeletonRow key={i} />)
          : jobs.map((job) => (
              <DatatableRow
                key={job.id}
                interactive
                className="h-20 motion-safe:transition-[background-color] motion-safe:duration-fast motion-safe:ease-out"
                onClick={() => onOpenJob(job)}
                data-testid={`delivery-job-row-${job.id}`}
              >
                <DatatableCell>
                  <TruncatedText
                    value={job.title}
                    className="text-sm font-medium text-foreground"
                  />
                </DatatableCell>

                <DatatableCell>
                  <JobSourceLink job={job} onDownload={onDownloadSource} />
                </DatatableCell>

                <DatatableCell>
                  <div className="flex items-center gap-1.5">
                    {job.platforms.map((platform) => (
                      <img
                        key={platform}
                        src={CONNECTOR_PLATFORM_ICONS[platform]}
                        alt={CONNECTOR_PLATFORM_LABELS[platform]}
                        title={CONNECTOR_PLATFORM_LABELS[platform]}
                        className="size-6 shrink-0 rounded-sm object-contain"
                      />
                    ))}
                  </div>
                </DatatableCell>

                {/* The plan as it stands, not as the file arrived. A job uploaded with
                  four sheets read sixteen rows and still said sixteen after three of
                  those platforms were dropped. */}
                <DatatableCell className="text-sm text-muted-foreground">
                  {t('deliveryJobGrid.deliveryJobGrid.adCount', { count: job.adCount })}
                </DatatableCell>

                <DatatableCell>
                  <Badge
                    variant={STATUS_VARIANT[job.status]}
                    roundness="full"
                    className="font-normal"
                  >
                    {JOB_STATUS_LABELS[job.status]}
                  </Badge>
                </DatatableCell>

                <DatatableCell className="text-sm text-muted-foreground">
                  {formatShortDate(job.updatedAt)}
                </DatatableCell>

                <DatatableCell className="text-right">
                  {onDeleteJob && <DeliveryJobRowActions job={job} onDelete={onDeleteJob} />}
                </DatatableCell>
              </DatatableRow>
            ))}
      </DatatableBody>
    </Datatable>
  );
}
